import { Button } from "@car/ui/components/button";
import { FileDropzone, UploadList, useUploads } from "@car/ui/components/file-upload";
import { FormAlert, useAppForm } from "@car/ui/components/form";
import { requiredChoice, requiredText } from "@car/ui/lib/form-rules";
import { cn, SEP } from "@car/ui/lib/utils";
import {
  ArrowRight01Icon,
  BubbleChatIcon,
  CustomerSupportIcon,
  HelpCircleIcon,
  SadDizzyIcon,
  TaskDone01Icon,
} from "@hugeicons-pro/core-stroke-rounded";
import { HugeiconsIcon, type IconSvgElement } from "@hugeicons/react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, createFileRoute, useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { toast } from "sonner";
import { z } from "zod";

import { openLiveChat } from "@/lib/live-chat-open";
import { PageHeader } from "@/components/panel-bits";
import { useSiteContent } from "@/lib/content";
import { faDate, toFaDigits } from "@/lib/format";
import { MAX_TICKET_FILES, ticketTypeKeys, type TicketTypeKey } from "@/lib/panel";
import { uploadPolicy } from "@/lib/uploads";
import { orpc } from "@/utils/orpc";

const isTicketType = (value: string): value is TicketTypeKey =>
  ticketTypeKeys.some((key) => key === value);

export const Route = createFileRoute("/panel/tickets/new")({
  head: () => ({ meta: [{ title: "تیکت تازه | پنل مشتریان تابان خودرو" }] }),
  // other pages link here with the ticket half filled in («افزودن خودرو به پرونده»)
  validateSearch: z.object({
    type: z.enum(ticketTypeKeys).optional().catch(undefined),
    subject: z.string().max(120).optional().catch(undefined),
  }),
  component: NewTicketPage,
});

const ticketFilePolicy = uploadPolicy("ticket-file");

const typeChoices: { key: TicketTypeKey; title: string; hint: string; icon: IconSvgElement }[] = [
  {
    key: "support",
    title: "پرسش یا راهنمایی",
    hint: "درباره خودرو، گارانتی، اقساط، سفارش یا کار با پنل سؤال دارید.",
    icon: HelpCircleIcon,
  },
  {
    key: "request",
    title: "درخواست",
    hint: "کاری باید انجام شود؛ مثل افزودن خودرو به پرونده، اصلاح مشخصات یا فاکتور.",
    icon: TaskDone01Icon,
  },
  {
    key: "complaint",
    title: "شکایت",
    hint: "از خدمات، تأخیر یا برخوردی ناراضی هستید؛ با اولویت بالاتر بررسی می‌شود.",
    icon: SadDizzyIcon,
  },
];

const ticketSchema = z.object({
  type: requiredChoice("نوع تیکت"),
  subject: requiredText("موضوع", { min: 3, max: 120 }),
  text: requiredText("شرح", { min: 5, max: 1000 }),
  vehicleId: z.string(),
  serviceId: z.string(),
});

function NewTicketPage() {
  const search = Route.useSearch();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [error, setError] = useState("");
  const options = useQuery(orpc.panel.tickets.list.queryOptions()).data?.options ?? {
    vehicles: [],
    invoices: [],
  };
  const uploads = useUploads({ policy: ticketFilePolicy, maxFiles: MAX_TICKET_FILES });
  const mutation = useMutation(orpc.panel.tickets.create.mutationOptions());

  const form = useAppForm({
    defaultValues: {
      type: search.type ?? "",
      subject: search.subject ?? "",
      text: "",
      vehicleId: "",
      serviceId: "",
    },
    validators: { onBlur: ticketSchema },
    onSubmit: async ({ value }) => {
      if (!isTicketType(value.type)) return;
      setError("");
      const result = await mutation
        .mutateAsync({
          type: value.type,
          subject: value.subject.trim(),
          text: value.text.trim(),
          vehicleId: value.vehicleId || null,
          serviceId: value.serviceId || null,
          fileIds: uploads.items.flatMap((item) => item.result?.id ?? []),
        })
        .catch(() => null);
      if (!result) return setError("ثبت تیکت انجام نشد؛ دوباره تلاش کنید.");
      if (!result.ok) return setError(result.reason);
      toast.success(`تیکت با شماره پیگیری ${toFaDigits(String(result.number))} ثبت شد`);
      await queryClient.invalidateQueries({ queryKey: orpc.panel.tickets.key() });
      void navigate({
        to: "/panel/tickets/$ticketId",
        params: { ticketId: result.ticketId },
        replace: true,
      });
    },
  });

  const vehicleOptions = [
    { value: "", label: "— هیچ‌کدام —" },
    ...options.vehicles.map((item) => ({
      value: item.id,
      label: item.plate ? `${item.name}${SEP}${toFaDigits(item.plate)}` : item.name,
    })),
  ];
  const invoiceOptions = [
    { value: "", label: "— هیچ‌کدام —" },
    ...options.invoices.map((item) => ({
      value: item.id,
      label: [
        item.title,
        item.invoiceNumber ? `فاکتور ${toFaDigits(item.invoiceNumber)}` : "",
        faDate(item.dateIso),
      ]
        .filter(Boolean)
        .join(SEP),
    })),
  ];

  return (
    <div className="mx-auto grid w-full max-w-5xl gap-5">
      <Button
        variant="ghost"
        size="sm"
        className="justify-self-start"
        nativeButton={false}
        render={<Link to="/panel/tickets" />}
      >
        <HugeiconsIcon icon={ArrowRight01Icon} strokeWidth={1.8} />
        همه تیکت‌ها
      </Button>
      <PageHeader
        title="تیکت تازه"
        description="موضوع را انتخاب و شرح دهید؛ پاسخ همین‌جا و با پیامک به شما می‌رسد."
      />

      <div className="grid items-start gap-6 lg:grid-cols-[minmax(0,1fr)_17rem]">
        <form.AppForm>
          <form.Form className="grid gap-6 rounded-2xl border bg-card p-4 sm:p-6">
            <form.AppField name="type">
              {(field) => (
                <field.FormField label="درباره چیست؟">
                  {({ controlProps }) => (
                    <div
                      role="radiogroup"
                      id={controlProps.id}
                      aria-invalid={controlProps["aria-invalid"]}
                      aria-describedby={controlProps["aria-describedby"]}
                      className="grid gap-2 sm:grid-cols-3"
                    >
                      {typeChoices.map((choice) => {
                        const checked = field.state.value === choice.key;
                        return (
                          <button
                            key={choice.key}
                            type="button"
                            role="radio"
                            aria-checked={checked}
                            onClick={() => {
                              field.handleChange(choice.key);
                              field.handleBlur();
                            }}
                            className={cn(
                              "grid content-start gap-1.5 rounded-xl border p-3.5 text-start transition-[border-color,background-color,box-shadow] outline-none hover:border-primary/40 focus-visible:ring-3 focus-visible:ring-ring/40",
                              checked &&
                                "border-primary bg-primary/5 shadow-[inset_0_0_0_1px_var(--primary)]",
                            )}
                          >
                            <span className="flex items-center gap-2">
                              <HugeiconsIcon
                                icon={choice.icon}
                                strokeWidth={1.8}
                                className={cn(
                                  "size-5",
                                  checked ? "text-primary" : "text-muted-foreground",
                                )}
                              />
                              <span className="font-black">{choice.title}</span>
                            </span>
                            <span className="text-xs leading-5 text-muted-foreground">
                              {choice.hint}
                            </span>
                          </button>
                        );
                      })}
                    </div>
                  )}
                </field.FormField>
              )}
            </form.AppField>

            <form.AppField name="subject">
              {(field) => (
                <field.TextField
                  label="موضوع"
                  maxLength={120}
                  placeholder="در یک جمله؛ مثلاً «پیگیری وضعیت گارانتی»"
                />
              )}
            </form.AppField>

            {options.vehicles.length > 0 || options.invoices.length > 0 ? (
              <div className="grid gap-4 sm:grid-cols-2">
                {options.vehicles.length > 0 ? (
                  <form.AppField name="vehicleId">
                    {(field) => (
                      <field.SelectField label="خودروی مرتبط (اختیاری)" options={vehicleOptions} />
                    )}
                  </form.AppField>
                ) : null}
                {options.invoices.length > 0 ? (
                  <form.AppField name="serviceId">
                    {(field) => (
                      <field.SelectField label="فاکتور مرتبط (اختیاری)" options={invoiceOptions} />
                    )}
                  </form.AppField>
                ) : null}
              </div>
            ) : null}

            <form.AppField name="text">
              {(field) => (
                <field.TextareaField
                  label="شرح"
                  maxLength={1000}
                  rows={6}
                  placeholder="چه پیش آمده، از کی و چه انتظاری دارید؟ هر جزئیاتی که بنویسید، پاسخ را سریع‌تر می‌کند."
                />
              )}
            </form.AppField>

            <div className="grid gap-2">
              <FileDropzone
                uploads={uploads}
                multiple
                maxFiles={MAX_TICKET_FILES}
                variant="compact"
                title="افزودن عکس یا PDF (اختیاری)"
              />
              <UploadList uploads={uploads} />
            </div>

            <FormAlert message={error} />
            <div className="flex flex-wrap items-center justify-end gap-3 border-t pt-4">
              <Button
                type="button"
                variant="ghost"
                nativeButton={false}
                render={<Link to="/panel/tickets" />}
              >
                انصراف
              </Button>
              <form.SubmitButton disabled={uploads.busy} pending={mutation.isPending}>
                ثبت تیکت
              </form.SubmitButton>
            </div>
          </form.Form>
        </form.AppForm>

        <Tips />
      </div>
    </div>
  );
}

function Tips() {
  const { liveChat } = useSiteContent();
  return (
    <aside className="grid gap-4 text-sm lg:sticky lg:top-20">
      <section className="grid gap-2">
        <h2 className="flex items-center gap-2 font-black">
          <HugeiconsIcon
            icon={CustomerSupportIcon}
            strokeWidth={1.8}
            className="size-4.5 text-primary"
          />
          چه بنویسیم که زودتر حل شود؟
        </h2>
        <ul className="grid list-disc gap-1.5 ps-5 leading-6 text-muted-foreground">
          <li>خودرو یا فاکتور مرتبط را انتخاب کنید تا کارشناس پرونده را جلوی چشم داشته باشد.</li>
          <li>عکس عیب، صفحه خطا یا رسید پرداخت را پیوست کنید.</li>
          <li>برای هر موضوع یک تیکت؛ پاسخ‌ها جدا و روشن می‌مانند.</li>
        </ul>
      </section>
      {liveChat.enabled !== "off" ? (
        <section className="grid gap-2 border-t pt-4">
          <p className="leading-6 text-muted-foreground">
            سؤال کوتاهی است که همین حالا جواب می‌خواهد؟ در گفتگوی آنلاین بپرسید.
          </p>
          <Button type="button" variant="outline" className="justify-start" onClick={openLiveChat}>
            <HugeiconsIcon icon={BubbleChatIcon} strokeWidth={1.8} />
            گفتگوی آنلاین
          </Button>
        </section>
      ) : null}
    </aside>
  );
}
